import { Button, Icon, OptionType, SearchableSelect, Textinput } from "shared";
import React, { useEffect } from "react";
import { TComponentsTranslationPaths, useComponentsTranslations } from "../../../../../lib/i18n";
import {
    defaultBankDetailsFilter,
    FilterLogic,
    FilterOperator,
    type ILegalListFilter,
    type ILegalListFilterRow,
    type FilterLogicType,
    type FilterOperatorType,
} from "./types";
import styles from "./list_filter.module.scss";

const LEGAL_ORG_BANK_DETAIL_FILTER_FIELDS = [
    "ya_account_currency",
    "ya_account_type",
    "ya_bank_name",
    "ya_bic",
    "ya_payment_account",
    "ya_corr_account",
] as const;

const LOGIC_OPTIONS = [
    { value: FilterLogic.and, label: "" },
    { value: FilterLogic.or, label: "" },
];

const OPERATOR_OPTIONS = [
    { value: FilterOperator.equals, label: "" },
    { value: FilterOperator.contains, label: "" },
    { value: FilterOperator.notEquals, label: "" },
    { value: FilterOperator.notContains, label: "" },
    { value: FilterOperator.empty, label: "" },
    { value: FilterOperator.notEmpty, label: "" },
];

const operatorHidesValue = (operator: FilterOperatorType) =>
    operator === FilterOperator.empty || operator === FilterOperator.notEmpty;

interface IListFilterProps {
    value: ILegalListFilter;
    onChange: (value: ILegalListFilter) => void;
}

const createEmptyRow = (): ILegalListFilterRow => ({
    field: "ya_account_currency",
    operator: FilterOperator.contains,
    value: "",
});

export const LegalCompanyListFilter: React.FC<IListFilterProps> = React.memo(({ value, onChange }) => {
    const t = useComponentsTranslations();
    const { logic, rows: valueRows } = value ?? defaultBankDetailsFilter;
    const rows = valueRows?.length > 0 ? valueRows : [createEmptyRow()];

    useEffect(() => {
        const { logic: l, rows: vr } = value ?? defaultBankDetailsFilter;
        const list = vr?.length > 0 ? vr : [createEmptyRow()];
        if (!list.some((row) => operatorHidesValue(row.operator) && row.value !== "")) {
            return;
        }
        onChange({
            logic: l,
            rows: list.map((row) =>
                operatorHidesValue(row.operator) ? { ...row, value: "" } : row
            ),
        });
    }, [value, onChange]);

    const logicOptionsWithLabels = LOGIC_OPTIONS.map((o) => ({
        ...o,
        label: o.value === FilterLogic.and ? t("filterLogicAnd") : t("filterLogicOr"),
    }));

    const operatorLabels: Record<FilterOperatorType, string> = {
        [FilterOperator.equals]: t("filterOperatorEquals"),
        [FilterOperator.contains]: t("filterOperatorContains"),
        [FilterOperator.notEquals]: t("filterOperatorNotEquals"),
        [FilterOperator.notContains]: t("filterOperatorNotContains"),
        [FilterOperator.empty]: t("filterOperatorEmpty"),
        [FilterOperator.notEmpty]: t("filterOperatorNotEmpty"),
    };
    const operatorOptionsWithLabels = OPERATOR_OPTIONS.map((o) => ({
        ...o,
        label: operatorLabels[o.value as FilterOperatorType],
    }));

    const getFieldLabel = (fieldKey: string) =>
        t(`legalYandexFields.${fieldKey}` as TComponentsTranslationPaths);

    const fieldOptionsWithLabels: OptionType[] = LEGAL_ORG_BANK_DETAIL_FILTER_FIELDS.map((field) => ({
        value: field,
        label: getFieldLabel(field),
    }));

    const setLogic = (newLogic: FilterLogicType) => {
        onChange({ logic: newLogic, rows });
    };

    const updateRow = (index: number, patch: Partial<ILegalListFilterRow>) => {
        const next = [...rows];
        next[index] = { ...next[index], ...patch };
        onChange({ logic, rows: next });
    };

    const removeRow = (index: number) => {
        const next = rows.filter((_, i) => i !== index);
        onChange({ logic, rows: next.length > 0 ? next : [createEmptyRow()] });
    };

    const addRow = () => {
        onChange({ logic, rows: [...rows, createEmptyRow()] });
    };

    const inner = (
            <div className={styles.container}>
                {rows.map((row, index) => (
                    <div key={index} className={styles.filterRow}>
                        <Button
                            view="ghost"
                            className={styles.removeButton}
                            onClick={() => removeRow(index)}
                        >
                            <Icon size="m" glyph="close"/>
                        </Button>
                        {index > 0 && (
                            <div className={styles.logicSelect}>
                                <SearchableSelect
                                    options={logicOptionsWithLabels}
                                    optionValue={
                                        logicOptionsWithLabels.find((o) => o.value === logic) ?? null
                                    }
                                    onOptionChange={(o) =>
                                        setLogic((o as OptionType | null)?.value as FilterLogicType)
                                    }
                                    isMulti={false}
                                    isSearchable={false}
                                    hideClearIcon={true}
                                    disabled={index > 1}
                                />
                            </div>
                        )}
                        <div className={styles.fieldSelect}>
                            <SearchableSelect
                                options={fieldOptionsWithLabels}
                                optionValue={
                                    fieldOptionsWithLabels.find((o) => o.value === row.field) ?? null
                                }
                                onOptionChange={(o) => updateRow(index, { field: (o as OptionType | null)?.value ?? row.field })}
                                isMulti={false}
                                isSearchable={true}
                                placeholder={t("listFilterFieldPlaceholder")}
                            />
                        </div>
                        <div className={styles.operatorSelect}>
                            <SearchableSelect
                                options={operatorOptionsWithLabels}
                                optionValue={
                                    operatorOptionsWithLabels.find((o) => o.value === row.operator) ?? null
                                }
                                onOptionChange={(o) => {
                                    const nextOp =
                                        ((o as OptionType | null)?.value as FilterOperatorType) ?? row.operator;
                                    updateRow(index, {
                                        operator: nextOp,
                                        ...(operatorHidesValue(nextOp) ? { value: "" } : {}),
                                    });
                                }}
                                isMulti={false}
                                isSearchable={false}
                                hideClearIcon={true}
                            />
                        </div>
                        {!operatorHidesValue(row.operator) && (
                            <div className={styles.valueInput}>
                                <Textinput
                                    size="m"
                                    view="outline"
                                    value={row.value}
                                    onChange={(e) => updateRow(index, { value: e.target.value })}
                                    placeholder={t("listFilterValuePlaceholder")}
                                />
                            </div>
                        )}
                    </div>
                ))}
                <Button
                    view="outline"
                    size="s"
                    className={styles.addRowButton}
                    onClick={addRow}
                >
                    {t("listFilterAddRow")}
                </Button>
            </div>
    );

    return inner;
});
